import { PUNCH_DONE_STATUSES, type ProjectStatus, type PunchStatus, type StageStatus } from "@sagot/constants";
import type { PunchFilter, PunchSortKey } from "./installation-constants";
import type { PunchOut, ReadinessOut, StageOut } from "./installation-types";

/** Why a stage cannot move, as the server's `stage_blocked` reasons name them. */
export type StageViolation = "previous_not_passed" | "later_stage_started" | "checklist_incomplete" | "not_allowed";

/** Where each stage state may go (mirror of the server's stage machine: pending -> in progress -> passed or failed). */
const STAGE_MOVES: Readonly<Record<StageStatus, readonly StageStatus[]>> = {
  pending: ["in_progress"],
  in_progress: ["passed", "failed", "pending"],
  failed: ["in_progress"],
  passed: ["in_progress"],
};

export function stageMoves(status: StageStatus): readonly StageStatus[] {
  return STAGE_MOVES[status];
}

type StageFacts = Pick<StageOut, "status" | "parallel" | "checklist">;

/**
 * Why stage `index` may not move to `target` (`null`: it may), the same rules the server enforces so the stepper can say
 * "blocked" before anyone clicks: starting needs the stage before it passed (unless it runs in parallel); reopening a
 * passed stage needs every later stage still pending; passing needs the checklist done.
 */
export function stageViolation(stages: readonly StageFacts[], index: number, target: StageStatus): StageViolation | null {
  const stage = stages[index];
  if (!stage) return "not_allowed";
  if (!STAGE_MOVES[stage.status].includes(target)) return "not_allowed";
  if (target === "in_progress") {
    if (stage.status === "passed" && stages.slice(index + 1).some((later) => later.status !== "pending" && !later.parallel)) return "later_stage_started";
    const before = stages[index - 1];
    if (index > 0 && !stage.parallel && before && before.status !== "passed") return "previous_not_passed";
  }
  if (target === "passed" && stage.checklist.some((item) => !item.done)) return "checklist_incomplete";
  return null;
}

/** The stage the work is at: the first one not passed (or the last when every stage passed). */
export function currentStageIndex(stages: readonly Pick<StageOut, "status">[]): number {
  const open = stages.findIndex((stage) => stage.status !== "passed");
  return open === -1 ? Math.max(stages.length - 1, 0) : open;
}

/** A stage label in the viewer's language, else English. */
export function labelIn(label: { en: string; fil?: string | null }, language: string): string {
  return language === "fil" && label.fil ? label.fil : label.en;
}

export interface BlockerLine {
  kind: "stage_not_passed" | "punch_open";
  /** The stage label for a stage blocker. */
  stage?: string;
  count?: number;
}

/** The lines of the acceptance panel's "not ready" list: stages that have not passed (by label) and how many items are still to verify. */
export function blockerLines(readiness: Pick<ReadinessOut, "blockers">, stages: readonly Pick<StageOut, "key" | "label">[], language: string): BlockerLine[] {
  return readiness.blockers.map((blocker) => {
    if (blocker.kind === "punch_open") return { kind: "punch_open", count: blocker.count };
    const stage = stages.find((candidate) => candidate.key === blocker.key);
    return { kind: "stage_not_passed", stage: stage ? labelIn(stage.label, language) : (blocker.key ?? "") };
  });
}

/** Which acceptance state a project is in. */
export type AcceptanceState = "blocked" | "ready" | "awaiting" | "accepted" | "paused" | "cancelled";

export function acceptanceState(project: { status: ProjectStatus; readiness: Pick<ReadinessOut, "ready"> }): AcceptanceState {
  switch (project.status) {
    case "handed_over":
      return "accepted";
    case "ready_for_acceptance":
      return "awaiting";
    case "on_hold":
      return "paused";
    case "cancelled":
      return "cancelled";
    case "punch_list":
      return project.readiness.ready ? "ready" : "blocked";
    default:
      return "blocked";
  }
}

export function isDone(status: PunchStatus): boolean {
  return (PUNCH_DONE_STATUSES as readonly string[]).includes(status);
}

export interface PunchCounts {
  all: number;
  open: number;
  in_progress: number;
  fixed: number;
  verified: number;
  reopened: number;
  waived: number;
}

export function punchCounts(items: readonly Pick<PunchOut, "status">[]): PunchCounts {
  const counts: PunchCounts = { all: items.length, open: 0, in_progress: 0, fixed: 0, verified: 0, reopened: 0, waived: 0 };
  for (const item of items) counts[item.status] += 1;
  return counts;
}

/** Items still to do: not verified and not waived. */
export function unfinishedCount(items: readonly Pick<PunchOut, "status">[]): number {
  return items.filter((item) => !isDone(item.status)).length;
}

export interface PunchQuery {
  status: PunchFilter;
  text: string;
  assigneeId: string;
}

export function filterPunch(items: readonly PunchOut[], query: PunchQuery): PunchOut[] {
  const needle = query.text.trim().toLowerCase();
  return items.filter(
    (item) =>
      (query.status === "all" || item.status === query.status) &&
      (query.assigneeId === "" || item.assignee?.id === query.assigneeId) &&
      (needle === "" || `${item.number} ${item.title} ${item.location ?? ""}`.toLowerCase().includes(needle)),
  );
}

const STATUS_ORDER: readonly PunchStatus[] = ["reopened", "open", "in_progress", "fixed", "verified", "waived"];

export function sortPunch(items: readonly PunchOut[], key: PunchSortKey, direction: "asc" | "desc"): PunchOut[] {
  const value = (item: PunchOut): string | number => {
    if (key === "title") return item.title.toLowerCase();
    if (key === "status") return STATUS_ORDER.indexOf(item.status);
    if (key === "assignee") return item.assignee?.name.toLowerCase() ?? "";
    return item.seq;
  };
  const sign = direction === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const left = value(a);
    const right = value(b);
    return (left < right ? -1 : left > right ? 1 : 0) * sign;
  });
}

/** The moves a staff member can make on an item from its status (mirror of the server's item machine; the server still decides). */
export type PunchMove = "start" | "fixed" | "verify" | "waive" | "reopen";

export function punchMoves(status: PunchStatus): readonly PunchMove[] {
  switch (status) {
    case "open":
    case "reopened":
      return ["start", "fixed", "waive"];
    case "in_progress":
      return ["fixed", "waive"];
    case "fixed":
      return ["verify", "reopen"];
    case "verified":
    case "waived":
      return ["reopen"];
    default:
      return [];
  }
}

export const MOVE_TARGET: Readonly<Record<PunchMove, PunchStatus>> = { start: "in_progress", fixed: "fixed", verify: "verified", waive: "waived", reopen: "reopened" };

/** Whether a move needs a typed reason (waive, a staff verification and a reopen) or a note / after photo (fixed). */
export function moveNeeds(move: PunchMove): "reason" | "evidence" | "nothing" {
  if (move === "waive" || move === "verify" || move === "reopen") return "reason";
  return move === "fixed" ? "evidence" : "nothing";
}
